<template>
  <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
    <div class="modal-content" ref="modalContentRef">
      <button class="close-button" @click="closeModal">
        <span class="close-icon">×</span>
      </button>
      
      <!-- Header del modal -->
      <div class="modal-header">
        <div class="header-left">
          <div class="logo-container">
            <img 
            :src="logo"
            :alt="name" 
            class="event-image"
          />
          </div>
          <div class="title-section">
            <h1 class="main-title">{{ convention?.name || 'CONVENCIONES 2025' }}</h1>
            <p class="subtitle">Agenda</p>
          </div>
        </div>
        <div class="header-right">
          <div class="info-label">
            <span class="label-text">FECHA: {{ formatDate(conventionDate) }}</span>
          </div>
          <div class="info-label">
            <span class="label-text">LUGAR: {{ conventionPlace }}</span>
          </div>
          <button class="download-button" @click="downloadPdf">
            Descargar PDF
          </button>
        </div>
      </div>


      <!-- Cronograma -->
      <div class="schedule-container">
        <div class="schedule-grid">
          <!-- Header de horarios -->
          <div class="time-header">
            <div class="room-label"></div>
            <div 
              v-for="timeSlot in timeSlots" 
              :key="timeSlot" 
              class="time-slot-header"
            >
              {{ timeSlot }}
            </div>
          </div> 
          <!-- Filas de jornadas -->
          <div 
            v-for="room in visibleRooms" 
            :key="room.id" 
            class="room-row"
          >
            <div class="room-label">
              <span class="room-name">{{ room.name }}</span>
            </div>
            <div 
              v-for="timeSlot in timeSlots" 
              :key="`${room.id}-${timeSlot}`" 
              class="time-cell"
            >
              <div 
                v-for="conference in getConferencesForRoomAndTime(room.id, timeSlot)"
                :key="conference.id"
                class="conference-block"
                :style="getConferenceBlockStyle(conference)"
                @click="openConferenceModal(conference)"
              >
                <div class="conference-title">{{ conference.name }}</div>
                <div class="conference-time">{{ formatConferenceTime(conference) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de conferencia individual -->
    <ConferenceModal
      v-if="selectedConference"
      :is-visible="!!selectedConference"
      :image="selectedConference.image"
      :name="selectedConference.name"
      :start_time="selectedConference.start_time"
      :description="selectedConference.information"
      :is-subscribed="selectedConference.is_subscribed"
      @close="closeConferenceModal"
      @subscribe-click="handleSubscribe"
    />
  </div>
</template>

<script setup>
import {defineProps, defineEmits, ref,computed } from "vue";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ConferenceModal from "./ConferenceModal.vue";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import ConferenceService from "@/services/api/conference";
//import config from "@/config";
const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true,
  },
  conferences: {
    type: Array,
    default: () => [],
  },
  convention: {
    type: Object,
    default: () => ({ name: "CONVENCIONES 2025" }),
  },
  conventionDate: {
    type: String,
    default: "18-08-2025",
  },
  conventionPlace: {
    type: String,
    default: "CÓRDOBA",
  },
});

const emit = defineEmits(['close']);

const selectedConference = ref(null);
const modalContentRef = ref(null);
const store = useStore();

const concessionaireId = computed(
  () => store.getters.getSelectedConcessionaire?.id
);

const handleSubscribe = async () => {
  try {
    if (!concessionaireId.value) {
      throw new Error("No se ha seleccionado un concesionario");
    }

    if (selectedConference.value.is_subscribed) {
      // Cancelar suscripción
      await ConferenceService.unsubscribe(
        selectedConference.value.id,
        concessionaireId.value
      );
      
      selectedConference.value.is_subscribed = false;
      
      Swal.fire({
        icon: "success",
        title: "¡Suscripción cancelada!",
        text: "Has cancelado tu registro a la conferencia",
        confirmButtonColor: "#021f5b",
      });
    } else {
      // Suscribirse
      await ConferenceService.subscribe(
        selectedConference.value.id,
        concessionaireId.value
      );
      
      selectedConference.value.is_subscribed = true;

      Swal.fire({
        icon: "success",
        title: "¡Inscripción exitosa!",
        text: "Te has registrado correctamente a la conferencia",
        confirmButtonColor: "#021f5b",
      });
    }
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Error en suscripción",
      text: error.message || "Ocurrió un error al intentar registrarse",
      confirmButtonColor: "#dc3545",
    });
  }
};

// Debug: verificar la estructura del objeto convention
console.log('ScheduleModal - Convention object:', props.convention);
console.log('ScheduleModal - Convention name:', props.convention?.name);
console.log('ScheduleModal - All props:', props);

const logo = computed(
  () =>
    require("@/assets/images/ACARA-logo-fondoblanco.png")
);
/*const imageUrl = computed(() => {

  return `${config.backendUrl}/${props.image}`;
});*/



// Definir las jornadas disponibles
const rooms = ref([

  { "id": 1, "name": "Jornada 1" },
  { "id": 2, "name": "Jornada 2" },
  { "id": 3, "name": "Jornada 3" },
  { "id": 4, "name": "Jornada 4" },
  { "id": 5, "name": "Jornada 5" },
  { "id": 6, "name": "Jornada 6" },
  { "id": 7, "name": "Jornada 7" },
  { "id": 8, "name": "Jornada 8" },
  { "id": 9, "name": "Jornada 9" },
  { "id": 10, "name": "Jornada 10" }
]);

// Normalizador de id de jornada desde la conferencia
const getConferenceRoomId = (conf) => {
  const candidates = [
    conf?.room_id,
    conf?.roomId,
    conf?.room,
    conf?.sala_id,
    conf?.salaId,
    conf?.sala,
    conf?.room?.id,
    conf?.room?.room_id
  ];
  for (const value of candidates) {
    if (value !== undefined && value !== null && value !== '') {
      return String(value);
    }
  }
  return null;
};

// Jornadas visibles: solo aquellas que tengan al menos una conferencia
const visibleRooms = computed(() => {
  if (!Array.isArray(props.conferences)) return rooms.value;
  const roomIdsWithConfs = new Set(
    props.conferences
      .map(getConferenceRoomId)
      .filter((id) => id !== null)
  );
  return rooms.value.filter((room) => roomIdsWithConfs.has(String(room.id)));
});

// Definir los slots de tiempo basados en los horarios reales de las conferencias
const timeSlots = ref([
  "08:00hs a 09:00hs",
  "09:00hs a 10:00hs",
  "10:00hs a 11:00hs",
  "11:00hs a 12:00hs",
  "12:00hs a 13:00hs",
  "13:00hs a 14:00hs",
  "14:00hs a 15:00hs",
  "15:00hs a 16:00hs",
  "16:00hs a 17:00hs",
  "17:00hs a 18:00hs",
  "18:00hs a 19:00hs",
  "19:00hs a 20:00hs",
  "20:00hs a 21:00hs"
]);

// Función para formatear la fecha
const formatDate = (dateString) => {
  if (!dateString) return "18-08-2025";
  return dateString;
};

// Función para obtener conferencias para una sala y horario específicos
const getConferencesForRoomAndTime = (roomId, timeSlot) => {
  const result = props.conferences.filter(conference => {
    const startTime = conference.start_time;
    
    if (!startTime) return false;
    
    // Verificar que la conferencia pertenece a esta jornada
    const conferenceRoomId = getConferenceRoomId(conference);
    if (!conferenceRoomId || conferenceRoomId !== String(roomId)) {
      return false;
    }
    
    // Extraer la hora del start_time (formato: "09:00:00")
    const hour = parseInt(startTime.split(':')[0]);
    const minute = parseInt(startTime.split(':')[1]);
    
    // Extraer el horario del slot (ej: "09:00hs a 10:00hs")
    const slotParts = timeSlot.split(' a ');
    const slotStart = slotParts[0].replace('hs', '').trim();
    const slotEnd = slotParts[1].replace('hs', '').trim();
    
    const slotStartHour = parseInt(slotStart.split(':')[0]);
    const slotStartMin = parseInt(slotStart.split(':')[1]) || 0;
    const slotEndHour = parseInt(slotEnd.split(':')[0]);
    const slotEndMin = parseInt(slotEnd.split(':')[1]) || 0;
    
    // Verificar si la conferencia comienza en este slot de tiempo
    const conferenceStartMinutes = hour * 60 + minute;
    const slotStartMinutes = slotStartHour * 60 + slotStartMin;
    const slotEndMinutes = slotEndHour * 60 + slotEndMin;
    
    // Solo mostrar la conferencia en el slot donde comienza
    return conferenceStartMinutes >= slotStartMinutes && conferenceStartMinutes < slotEndMinutes;
  });
  
  return result;
};

// Función para obtener el estilo del bloque de conferencia
const getConferenceBlockStyle = (conference) => {
  const startTime = conference.start_time;
  if (!startTime) return {};
  
  const hour = parseInt(startTime.split(':')[0]);
  const minute = parseInt(startTime.split(':')[1]);
  
  // Calcular la duración basada en end_time si está disponible
  const startMinutes = hour * 60 + minute;
  let endMinutes = startMinutes + 60; // duración por defecto de 1 hora
  if (conference.end_time) {
    const endHour = parseInt(conference.end_time.split(':')[0]);
    const endMinute = parseInt(conference.end_time.split(':')[1]);
    endMinutes = endHour * 60 + endMinute;
    if (endMinutes <= startMinutes) {
      endMinutes = startMinutes + 60; // mínimo 1 hora si la hora de fin es inválida
    }
  }
  
  const duration = endMinutes - startMinutes; // duración exacta en minutos
  
  // Calcular la posición horizontal dentro del primer casillero
  const slotStartMinutes = Math.floor(startMinutes / 60) * 60; // Inicio del casillero donde empieza
  const offsetMinutes = startMinutes - slotStartMinutes;
  const offsetPercent = (offsetMinutes / 60) * 100;
  
  // Calcular la posición de fin dentro del último casillero
  const slotEndMinutes = Math.floor(endMinutes / 60) * 60; // Inicio del casillero donde termina
  const endOffsetMinutes = endMinutes - slotEndMinutes; // Minutos dentro del último casillero
  // Asegurarnos de que endOffsetMinutes esté entre 0 y 60
  const clampedEndOffset = Math.max(0, Math.min(60, endOffsetMinutes));
  const endOffsetPercent = (clampedEndOffset / 60) * 100; // Porcentaje del último casillero que ocupa
  
  // Calcular cuántos casilleros completos hay entre el inicio del primer casillero y el inicio del último
  const startSlot = Math.floor(startMinutes / 60);
  const endSlot = Math.floor(endMinutes / 60);
  const slotsBetween = endSlot - startSlot; // Diferencia entre slots (ej: 15 - 11 = 4)
  
  // Calcular el ancho total desde donde empieza el bloque (con marginLeft) hasta donde termina
  let totalWidthPercent;
  
  if (slotsBetween === 0) {
    // El evento está completamente dentro del mismo casillero
    // El ancho es simplemente la diferencia entre donde termina y donde empieza
    totalWidthPercent = endOffsetPercent - offsetPercent;
  } else {
    // El evento atraviesa múltiples casilleros
    // Lo que queda del primer casillero desde donde empieza hasta el final
    const remainingInFirstSlot = 100 - offsetPercent;
    
    // Casilleros completos entre el primero y el último
    const fullSlotsBetween = slotsBetween - 1;
    
    // Porcentaje del último casillero que ocupa
    totalWidthPercent = remainingInFirstSlot + (fullSlotsBetween * 100) + endOffsetPercent;
  }
  
  // Los bordes entre casilleros (1px cada uno) ya están en el layout
  // Solo necesitamos considerarlos si el bloque atraviesa múltiples casilleros
  let borderWidth = 0;
  if (slotsBetween > 0) {
    const totalSlotsCrossed = slotsBetween + (endOffsetPercent > 0 ? 1 : 0);
    borderWidth = Math.max(0, totalSlotsCrossed - 1) * 1;
  }
  const paddingWidth = 0;
  
  // Usar el mismo color celeste para todos los eventos
  const backgroundColor = '#d9eefc';
  
  // Calcular el ancho mínimo basado en la duración
  const minWidthPx = Math.max((duration / 60) * 120, 60); // Mínimo 60px
  
  return {
    width: `calc(${totalWidthPercent}% + ${borderWidth}px + ${paddingWidth}rem)`,
    marginLeft: `${offsetPercent}%`,
    backgroundColor: backgroundColor,
    border: `1px solid rgba(255, 255, 255, 0.2)`,
    minWidth: `${minWidthPx}px`, // Ancho mínimo basado en duración exacta
    position: 'absolute',
    left: 0,
    top: '0.75rem',
    height: 'calc(100% - 1.5rem)', // Altura que respeta el padding superior e inferior
    zIndex: 1,
  };
};

// Función para formatear el tiempo de la conferencia
const formatConferenceTime = (conference) => {
  const startTime = conference.start_time ? conference.start_time.slice(0, 5) : '--:--';
  const endTime = conference.end_time ? conference.end_time.slice(0, 5) : '--:--';
  return `${startTime} - ${endTime}`;
};

// Función para abrir el modal de conferencia
const openConferenceModal = (conference) => {
  selectedConference.value = conference;
};

// Función para cerrar el modal de conferencia
const closeConferenceModal = () => {
  selectedConference.value = null;
};

// Función para cerrar el modal principal
const closeModal = () => {
  emit('close');
};

// Descargar la grilla como PDF
const downloadPdf = async () => {
  try {
    const element = modalContentRef.value;
    if (!element) return;

    // Expandir temporalmente el contenido con scroll para que no se corte
    const scheduleEl = element.querySelector('.schedule-container');
    const scheduleGrid = element.querySelector('.schedule-grid');
    const previous = {
      modal: {
        maxHeight: element.style.maxHeight,
        maxWidth: element.style.maxWidth,
        width: element.style.width,
        overflow: element.style.overflow,
      },
      schedule: scheduleEl
        ? {
            overflow: scheduleEl.style.overflow,
            maxHeight: scheduleEl.style.maxHeight,
            maxWidth: scheduleEl.style.maxWidth,
            width: scheduleEl.style.width,
            height: scheduleEl.style.height,
          }
        : null,
      grid: scheduleGrid
        ? {
            width: scheduleGrid.style.width,
            minWidth: scheduleGrid.style.minWidth,
          }
        : null,
    };

    // Expandir el modal para que muestre todo el contenido
    element.style.maxHeight = 'none';
    element.style.maxWidth = 'none';
    element.style.width = 'auto';
    element.style.overflow = 'visible';
    
    if (scheduleEl) {
      // Guardar el scroll actual
     // const scrollLeft = scheduleEl.scrollLeft;
      //const scrollTop = scheduleEl.scrollTop;
      
      scheduleEl.style.overflow = 'visible';
      scheduleEl.style.maxHeight = 'none';
      scheduleEl.style.maxWidth = 'none';
      scheduleEl.style.width = 'auto';
      scheduleEl.style.height = scheduleEl.scrollHeight + 'px';
      
      // Resetear el scroll para capturar desde el inicio
      scheduleEl.scrollLeft = 0;
      scheduleEl.scrollTop = 0;
    }
    
    if (scheduleGrid) {
      const gridWidth = scheduleGrid.scrollWidth;
      scheduleGrid.style.width = gridWidth + 'px';
      scheduleGrid.style.minWidth = gridWidth + 'px';
      
      // Ajustar el ancho del modal si es necesario
      if (gridWidth > element.scrollWidth) {
        element.style.width = gridWidth + 100 + 'px'; // Agregar padding extra
      }
    }

    // Esperar un momento para que el DOM se actualice
    await new Promise(resolve => setTimeout(resolve, 200));

    // Asegurar fondo blanco y buena resolución
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      width: element.scrollWidth,
      height: element.scrollHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
    });

    // Restaurar estilos originales
    element.style.maxHeight = previous.modal.maxHeight || '';
    element.style.maxWidth = previous.modal.maxWidth || '';
    element.style.width = previous.modal.width || '';
    element.style.overflow = previous.modal.overflow || '';
    
    if (scheduleEl && previous.schedule) {
      scheduleEl.style.overflow = previous.schedule.overflow || '';
      scheduleEl.style.maxHeight = previous.schedule.maxHeight || '';
      scheduleEl.style.maxWidth = previous.schedule.maxWidth || '';
      scheduleEl.style.width = previous.schedule.width || '';
      scheduleEl.style.height = previous.schedule.height || '';
    }
    
    if (scheduleGrid && previous.grid) {
      scheduleGrid.style.width = previous.grid.width || '';
      scheduleGrid.style.minWidth = previous.grid.minWidth || '';
    }

    const imgData = canvas.toDataURL("image/png");

    const orientation = canvas.width > canvas.height ? "landscape" : "portrait";
    const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const margin = 24; // margen pequeño
    const scale = Math.min(
      (pageWidth - margin * 2) / imgWidth,
      (pageHeight - margin * 2) / imgHeight
    );

    const renderWidth = imgWidth * scale;
    const renderHeight = imgHeight * scale;

//    let positionY = margin;
    let remainingHeight = renderHeight;

    // Si la imagen entra en una página, colocar directamente
    if (remainingHeight <= pageHeight - margin * 2) {
      const offsetX = (pageWidth - renderWidth) / 2;
      pdf.addImage(imgData, "PNG", offsetX, margin, renderWidth, renderHeight, undefined, "FAST");
    } else {
      // Paginación vertical: vamos cortando por alto de página
      const pageDrawableHeight = pageHeight - margin * 2;
      const canvasPageHeight = pageDrawableHeight / scale;

      let remainingCanvasY = 0;
      let firstPage = true;
      while (remainingHeight > 0) {
        if (!firstPage) pdf.addPage();
        firstPage = false;

        // Crear un canvas temporal con el recorte correspondiente
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(canvas.height - remainingCanvasY, canvasPageHeight);
        const ctx = pageCanvas.getContext("2d");
        if (!ctx) break;
        ctx.drawImage(
          canvas,
          0,
          remainingCanvasY,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          canvas.width,
          pageCanvas.height
        );

        const pageImg = pageCanvas.toDataURL("image/png");
        const pageRenderHeight = pageCanvas.height * scale;
        const offsetX = (pageWidth - renderWidth) / 2;
        pdf.addImage(pageImg, "PNG", offsetX, margin, renderWidth, pageRenderHeight, undefined, "FAST");

        remainingCanvasY += pageCanvas.height;
        remainingHeight -= pageRenderHeight;
      }
    }

    const fileName = `agenda_${(props.convention?.name || 'convencion').replace(/\s+/g, '_')}.pdf`;
    pdf.save(fileName);
  } catch (error) {
    console.error("Error al generar el PDF:", error);
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

.modal-content {
  background-color: white;
  border-radius: 12px;
  max-width: 95vw;
  width: 1400px;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 501;
  cursor: pointer;
}

.close-button:hover {
  background: #f8f8f8;
  transform: scale(1.1);
  border-color: #007bff;
}

.close-icon {
  font-size: 28px;
  line-height: 1;
  color: #333;
  margin-top: -2px;
  transition: color 0.3s ease;
}

.close-button:hover .close-icon {
  color: #007bff;
}

.download-button {
  background: #016fd5;
  color: #ffffff;
  border: none;
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.download-button:hover {
  background: #015bb0;
  transform: translateY(-1px);
}

/* Header del modal */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.logo-container {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
 
}

.event-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.title-section {
  display: flex;
  flex-direction: column;
}

.main-title {
  color: #021f5b;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 1px;
}

.subtitle {
  color: blue;
  font-size: 1rem;
  margin: 0;
  margin-top: 0.25rem;
  font-weight: bold;
}

.header-right {
   display: flex;
  gap: 1rem;
}

.info-label {
  background: #e9ecef;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  text-align: center;
}

.label-text {
  color: #495057;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Cronograma */
.schedule-container {
  flex: 1;
  overflow: auto;
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.schedule-grid {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: fit-content;
  border: 1px solid #dee2e6;
  box-sizing: border-box;
}

.time-header {
  display: flex;
  align-items: stretch;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  min-width: fit-content;
  box-sizing: border-box;
}

.room-label {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  box-sizing: border-box;
}

.room-name {
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.room-line {
  font-size: 0.8rem;
  color: #6c757d;
}

.time-slot-header {
  flex: 1;
  min-width: 120px;
  padding: 1rem 0.5rem;
  text-align: center;
  font-weight: 600;
  color: #495057;
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
  font-size: 0.9rem;
  box-sizing: border-box;
}

.time-slot-header:last-child {
  border-right: 1px solid #dee2e6;
}

.room-row {
  display: flex;
  align-items: stretch;
  min-height: 100px;
  overflow: visible;
  position: relative;
  width: 100%;
  min-width: fit-content;
  box-sizing: border-box;
  border-bottom: 1px solid #dee2e6;
}

.room-row::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: #dee2e6;
  pointer-events: none;
}

.room-row .time-cell:last-child {
  border-right: 1px solid #dee2e6;
}

.time-cell {
  flex: 1;
  min-width: 120px;
  padding: 0.75rem;
  border-right: 1px solid #dee2e6;
  border-left: none;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-height: 100px;
  overflow: visible;
  box-sizing: border-box;
}

.conference-block {
  background: #87CEEB;
  color: white;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.conference-block:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  background: #98D8F0;
}

.conference-title {
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  color: #016fd5;
}

.conference-time {
  font-size: 0.65rem;
  font-weight: 500;
  color:#016fd5;
  text-align: center;
  margin-top: 0.25rem;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    max-width: 98vw;
    max-height: 95vh;
  }
  
  .modal-header {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  
  .header-left {
    flex-direction: column;
    text-align: center;
  }
  
  .main-title {
    font-size: 1.5rem;
  }
  
  .time-slot-header {
    font-size: 0.7rem;
    padding: 0.5rem 0.25rem;
  }
  
  .room-label {
    min-width: 80px;
    padding: 0.5rem;
  }
  
  .conference-title {
    font-size: 0.7rem;
  }
  
  .conference-time {
    font-size: 0.6rem;
  }
}

@media (max-width: 480px) {
  .modal-content {
    max-width: 99vw;
    max-height: 98vh;
  }
  
  .schedule-container {
    padding: 0.5rem;
  }
  
  .time-slot-header {
    font-size: 0.6rem;
    padding: 0.25rem;
  }
  
  .room-label {
    min-width: 60px;
    padding: 0.25rem;
  }
  
  .conference-block {
    min-height: 50px;
    padding: 0.25rem;
  }
}
</style>
